// Loyalty V2 API functions (plain async, not hooks — the v2 module pattern,
// cf. booking-v2.ts). All endpoints scope by the JWT business.
import client from "shared/utils/axios-config";
import {
  CustomerLoyalty,
  LoyaltyConfigResponse,
  UpdateLoyaltyConfigForm,
  VoucherEligiblePackage,
} from "shared/types/loyalty";

export const getLoyaltyConfig = async (): Promise<LoyaltyConfigResponse> => {
  const res = await client.get(`/v2/loyalty/config`);
  return res.data.data;
};

export const updateLoyaltyConfig = async (
  form: UpdateLoyaltyConfigForm
): Promise<LoyaltyConfigResponse> => {
  const res = await client.put(`/v2/loyalty/config`, form);
  return res.data.data;
};

// Lifecycle (Manager-only): deactivate ends the campaign (open cards erased,
// config reset); saving a config is what activates.
export const deactivateLoyaltyProgram = async (): Promise<void> => {
  const res = await client.post(`/v2/loyalty/program/deactivate`);
  return res.data.data;
};

export const getCustomerLoyalty = async (
  customerId: string
): Promise<CustomerLoyalty> => {
  const res = await client.get(`/v2/loyalty/customers/${customerId}`);
  return res.data.data;
};

export const adjustCustomerStamps = async (args: {
  customerId: string;
  direction: "add" | "remove";
  reason: string;
}): Promise<CustomerLoyalty> => {
  const res = await client.post(
    `/v2/loyalty/customers/${args.customerId}/stamps/adjust`,
    { direction: args.direction, reason: args.reason }
  );
  return res.data.data;
};

export const getVoucherEligiblePackages = async (
  voucherId: string
): Promise<{ packages: VoucherEligiblePackage[] }> => {
  const res = await client.get(
    `/v2/loyalty/vouchers/${voucherId}/eligible-packages`
  );
  return res.data.data;
};

export const redeemVoucherCredit = async (voucherId: string) => {
  const res = await client.post(`/v2/loyalty/vouchers/${voucherId}/redeem-credit`);
  return res.data.data;
};

export const redeemVoucherDiscount = async (args: {
  voucherId: string;
  packageId: number;
  paymentMethodId?: string | null;
}) => {
  const res = await client.post(
    `/v2/loyalty/vouchers/${args.voucherId}/redeem-discount`,
    { package_id: args.packageId, payment_method_id: args.paymentMethodId ?? null }
  );
  return res.data.data;
};

export const voidVoucher = async (args: {
  voucherId: string;
  reason: string;
}) => {
  const res = await client.post(`/v2/loyalty/vouchers/${args.voucherId}/void`, {
    reason: args.reason,
  });
  return res.data.data;
};

// Claw-back of a CLAIMED free-credit reward: removes the untouched credit and
// puts the card one stamp under the milestone so it can be re-earned. The
// server enforces every guard (unused credit, no newer stamp).
export const voidClaimedVoucher = async (args: {
  voucherId: string;
  reason: string;
}) => {
  const res = await client.post(
    `/v2/loyalty/vouchers/${args.voucherId}/void-claimed`,
    { reason: args.reason }
  );
  return res.data.data;
};
